<script setup lang="ts">
import { computed } from 'vue';
import { useThemeVars } from 'naive-ui';
import GaiaTable from '../base/GaiaTable.vue';

// Every prop the caller sets falls through to the GaiaTable root, so this
// composite only owns the runs grid's own visual rules.
const themeVars = useThemeVars();
const groupDivider = computed(() => themeVars.value.dividerColor);
</script>

<template>
  <!-- The Playbook runs grid: a GaiaTable whose evaluation-type groups are
       separated by a vertical hairline, with cell content pinned to the top of
       the row so a wrapping cell (a two-line date, a chip over its trigger
       glyph) doesn't drift the rest of the row off the first line. -->
  <gaia-table class="gaia-runs-table">
    <template v-if="$slots.empty" #empty>
      <slot name="empty" />
    </template>
    <template v-if="$slots.loading" #loading>
      <slot name="loading" />
    </template>
  </gaia-table>
</template>

<style scoped>
/* Vertical divider marking where each evaluation-type group starts. */
.gaia-runs-table :deep(th.eval-group-start),
.gaia-runs-table :deep(td.eval-group-start) {
  border-left: 1px solid v-bind(groupDivider);
}

.gaia-runs-table :deep(.n-data-table-td) {
  vertical-align: top;
}

/* Each control centres inside its own box — 13px for the status dot, 28px
   for the delete button's circle — so top-aligning alone leaves them on
   different lines. Centre them on the row's first text line instead: 1.6em is
   the table's own line box (13px × naive's 1.6), in em so it tracks the
   theme. */
.gaia-runs-table :deep(.n-data-table-td) > * {
  vertical-align: middle;
}

/* The delete button keeps its 28px hit area (GaiaDeleteButton size="md"); the
   negative margins collapse its margin box back to the line so it can't grow
   the row's first line. */
.gaia-runs-table :deep(td.runs-actions-cell) > * {
  margin-block: calc((1.6em - 28px) / 2);
}
</style>
